<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte'
  import BrandHeader from './BrandHeader.svelte'
  import { playHoverSound, playSelectSound } from './sounds'
  import type { BrandImage, GameOption } from './types'
  import { darkenColor, hexToRgb } from './utils'

  export let options: GameOption[];
  export let selectionTitle: string;
  export let selectionBackgroundHex: string;
  export let image: BrandImage;
  export let cardTitle: string | undefined = undefined;

  const dispatch = createEventDispatcher<{ select: GameOption }>();

  let mounted = false;
  let selectedId: string | null = null;
  let isTransitioning = false;
  let timeouts: ReturnType<typeof setTimeout>[] = [];

  onMount(() => {
    // Trigger staggered entrance animation
    mounted = true;

    // Cleanup function to clear pending timeouts
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  });

  function handleSelect(option: GameOption, event: MouseEvent) {
    if (isTransitioning) return;

    const clickedElement = event.currentTarget as HTMLElement;

    window?.triggerNativeInteractionEvent?.(clickedElement, 'handle_select', { option: option.title });

    // Play selection sound
    playSelectSound();

    // Mark as selected and trigger premium click animation
    selectedId = option._id;
    isTransitioning = true;

    // Delay dispatch to allow selection animation to play
    const timeout = setTimeout(() => {
      dispatch('select', option);
    }, 200);
    timeouts.push(timeout);
  }

  function handleHover() {
    if (!isTransitioning) {
      playHoverSound();
    }
  }
</script>

<div
  class="selection-screen"
  style="--bg-color: {selectionBackgroundHex}; --bg-color-dark: {darkenColor(selectionBackgroundHex, 20)}; --bg-color-rgb: {hexToRgb(selectionBackgroundHex)};"
>
  <!-- Background pattern with screen blend mode -->
  <div class="bg-pattern" aria-hidden="true"></div>

  <BrandHeader {image} {cardTitle} backgroundHex={selectionBackgroundHex} />

  <div class="selection-content">
    <h2 class="selection-title">{selectionTitle}</h2>

    <div class="options-grid" class:transitioning={isTransitioning} role="group" aria-label="Game selection options">
    {#each options as option, index (option._id)}
      <button
        class="option-button"
        class:mounted
        class:selected={selectedId === option._id}
        class:not-selected={selectedId !== null && selectedId !== option._id}
        style="--btn-color: {option.backgroundHex}; --btn-color-dark: {darkenColor(option.backgroundHex, 15)}; --delay: {index * 0.05}s;"
        on:click={(event) => handleSelect(option, event)}
        on:mouseenter={handleHover}
        aria-label="Select {option.title}"
        disabled={isTransitioning}
      >
        <span class="option-emoji" aria-hidden="true">{option.emoji}</span>
        <span class="option-title">{option.title}</span>
      </button>
    {/each}
    </div>
  </div>
</div>

<style>
  .selection-screen {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 16px 24px;
    box-sizing: border-box;
    background: linear-gradient(135deg, var(--bg-color) 0%, var(--bg-color-dark) 100%);
    position: relative;
    overflow: hidden;
    container-type: size;
    container-name: selection-screen;
  }

  .selection-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    gap: 16px;
    width: 100%;
  }

  .bg-pattern {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    mix-blend-mode: screen;
    opacity: 0.15;
    pointer-events: none;
  }

  .selection-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #ffd700;
    text-align: center;
    position: relative;
    z-index: 1;
    text-transform: uppercase;
    letter-spacing: 2.5px;
    padding: 8px 22px;
    border-radius: 20px;
    backdrop-filter: blur(8px);
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.3));
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 215, 0, 0.2);
    text-shadow: 0 0 14px rgba(255, 215, 0, 0.5), 0 0 30px rgba(255, 215, 0, 0.2);
    overflow: hidden;
    animation: titleGlow 3s ease-in-out infinite;
  }

  .selection-title::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.12), transparent);
    animation: titleSweep 4s ease-in-out infinite;
    pointer-events: none;
  }

  .options-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    width: 100%;
    max-width: 700px;
    position: relative;
    z-index: 1;
  }

  .option-button {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 8px;
    border: none;
    border-radius: 6.4px;
    background: linear-gradient(145deg, var(--btn-color) 0%, var(--btn-color-dark) 100%);
    color: white;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    /* Initial state for staggered entrance */
    opacity: 0;
    transform: translateY(20px) scale(0.9);
    transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.2s ease;
    transition-delay: var(--delay);
    /* Subtle idle animation */
    animation: subtlePulse 3s ease-in-out infinite;
    animation-delay: var(--delay);
  }

  .option-button.mounted {
    opacity: 1;
    transform: translateY(0) scale(1);
    /* Remove delay after entrance animation completes */
    transition-delay: 0s;
  }

  /* Premium glare effect on game cards */
  .option-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.1) 30%,
      transparent 50%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
    border-radius: 6.4px;
  }

  /* Selection glow ring */
  .option-button::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 9.600000000000001px;
    background: transparent;
    border: 2px solid transparent;
    pointer-events: none;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .option-button.selected::after {
    border-color: rgba(255, 255, 255, 0.6);
    opacity: 1;
    animation: glowRing 0.3s ease-out forwards;
  }

  @keyframes glowRing {
    0% {
      transform: scale(0.9);
      opacity: 0;
    }
    50% {
      transform: scale(1.05);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .option-button:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow:
      0 8px 25px rgba(0, 0, 0, 0.35),
      0 0 20px rgba(255, 255, 255, 0.15),
      inset 0 0 20px rgba(255, 255, 255, 0.1);
    animation: none;
  }

  .option-button:active {
    transform: translateY(-1px) scale(0.98);
  }

  /* Premium selection animation */
  .option-button.selected {
    animation: selectPulse 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards !important;
    z-index: 10;
  }

  .option-button.not-selected {
    animation: fadeOutScale 0.2s ease-out forwards !important;
  }

  @keyframes selectPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }
    50% {
      transform: scale(1.15);
      box-shadow:
        0 8px 30px rgba(0, 0, 0, 0.4),
        0 0 40px rgba(255, 255, 255, 0.3),
        inset 0 0 30px rgba(255, 255, 255, 0.2);
    }
    100% {
      transform: scale(1.1);
      box-shadow:
        0 8px 25px rgba(0, 0, 0, 0.35),
        0 0 30px rgba(255, 255, 255, 0.25);
    }
  }

  @keyframes fadeOutScale {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0.3;
      transform: scale(0.95);
    }
  }

  @keyframes subtlePulse {
    0%, 100% {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    }
    50% {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), 0 0 8px rgba(255, 255, 255, 0.1);
    }
  }

  @keyframes titleGlow {
    0%, 100% {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 215, 0, 0.2);
      text-shadow: 0 0 14px rgba(255, 215, 0, 0.5), 0 0 30px rgba(255, 215, 0, 0.2);
    }
    50% {
      box-shadow: 0 2px 18px rgba(0, 0, 0, 0.4), inset 0 0 0 1px rgba(255, 215, 0, 0.35);
      text-shadow: 0 0 18px rgba(255, 215, 0, 0.7), 0 0 40px rgba(255, 215, 0, 0.3);
    }
  }

  @keyframes titleSweep {
    0%, 100% {
      left: -100%;
    }
    50% {
      left: 100%;
    }
  }

  .option-emoji {
    font-size: 24px;
    line-height: 1;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  .option-title {
    font-size: 13.600000000000001px;
    font-weight: 600;
    text-align: center;
    line-height: 1.2;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  /* Wide layout (1200x200) - single row */
  @container (min-aspect-ratio: 3/1) {
    .selection-screen {
      gap: 8px;
    }

    .options-grid {
      grid-template-columns: repeat(8, 1fr);
      max-width: 100%;
    }

    .option-button {
      padding: 8px 4px;
      min-height: 50px;
    }

    .option-emoji {
      font-size: 20px;
    }

    .option-title {
      font-size: 11.200000000000001px;
    }
  }

  /* Medium and narrow layouts - 2x4 grid */
  @container (max-aspect-ratio: 3/1) {
    .options-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  /* Very narrow - smaller text */
  @container (max-width: 500px) {
    .selection-title {
      font-size: 15px;
      padding: 7px 18px;
      letter-spacing: 2px;
    }

    .option-title {
      font-size: 12px;
    }
  }

  /* Extra narrow - 3x3x2 grid layout */
  @container (max-width: 400px) {
    .selection-title {
      font-size: 13px;
      padding: 6px 14px;
      letter-spacing: 1.5px;
    }

    .options-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .option-button {
      flex: 0 0 calc(33.333% - 6px);
      min-height: 60px;
    }

    /* Last 2 cards take 50% each */
    .option-button:nth-last-child(1),
    .option-button:nth-last-child(2) {
      flex: 0 0 calc(50% - 4px);
    }

    .option-emoji {
      display: none;
    }

    .option-title {
      font-size: 12px;
    }
  }

  /* Two row layout for short AND narrow containers (height 0-200px, width < 800px) */
  @container selection-screen (max-height: 200px) and (max-width: 799px) {
    .selection-screen {
      gap: 8px;
    }

    .options-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
    }

    .selection-title {
      font-size: 14px;
      padding: 6px 16px;
      letter-spacing: 2px;
    }

    .option-button {
      padding: 6.4px 4.800000000000001px;
      min-height: 50px;
    }

    .option-emoji {
      font-size: 16px;
    }

    .option-title {
      font-size: 9.600000000000001px;
    }
  }

  /* Single row layout for short AND wide containers (height 0-200px, width 800px+) */
  @container selection-screen (max-height: 200px) and (min-width: 800px) {
    .options-grid {
      grid-template-columns: repeat(8, 1fr);
      max-width: 100%;
    }
  }

  /* Ultra-short wide layout (height <= 150px, width >= 800px) - two row layout */
  @container selection-screen (min-aspect-ratio: 3/1) {
    .selection-screen {
      gap: 0;
      padding: 0 24px;
    }

    .selection-content {
      gap: 0;
      justify-content: space-evenly;
    }

    .selection-title {
      font-size: 14px;
      flex-shrink: 0;
      padding: 6px 18px;
      margin-bottom: 8px;
    }

    .options-grid {
      grid-template-columns: repeat(8, 1fr);
      gap: 8px;
      flex: none;
    }

    .option-button {
      padding: 12px 6px;
      min-height: unset;
      gap: 0;
    }

    .option-emoji {
      font-size: 16px;
      margin-bottom: 4px;
    }

    .option-title {
      font-size: 13px;
      line-height: 1.2;
    }
  }

  /* Height-based card sizing for taller containers (350px+) */
  @container selection-screen (min-height: 350px) {
    .option-button {
      padding: 16px 12px;
      gap: 10px;
    }

    .option-emoji {
      font-size: 28px;
    }

    .option-title {
      font-size: 14px;
    }
  }

  /* Tall + narrow: override for width < 400px */
  @container selection-screen (min-height: 350px) and (max-width: 400px) {
    .option-button {
      padding: 8px 6px;
      gap: 6px;
    }

    .option-emoji {
      display: none;
    }

    .option-title {
      font-size: 12px;
    }
  }

  /* Height-based card sizing for medium containers (250-350px) */
  @container selection-screen (min-height: 250px) and (max-height: 349px) {
    .option-button {
      padding: 12px 8px;
      gap: 8px;
    }

    .option-emoji {
      font-size: 24px;
    }

    .option-title {
      font-size: 13px;
    }
  }

  /* Medium height + narrow width (250-350px height, <=500px width) */
  @container selection-screen (min-height: 250px) and (max-height: 349px) and (max-width: 500px) {
    .selection-screen {
      gap: 12px;
    }

    .selection-title {
      font-size: 14px;
      padding: 6px 16px;
      letter-spacing: 2px;
    }

    .options-grid {
      gap: 8px;
    }

    .option-button {
      padding: 8px 6px;
      gap: 6px;
      min-height: 50px;
    }

    .option-emoji {
      font-size: 20px;
    }

    .option-title {
      font-size: 11px;
    }
  }

  /* Height-based card sizing for shorter containers (200-250px) */
  @container selection-screen (min-height: 200px) and (max-height: 249px) {
    .option-button {
      padding: 10px 6px;
      gap: 6px;
    }

    .option-emoji {
      font-size: 20px;
    }

    .option-title {
      font-size: 12px;
    }
  }
</style>
